import {
  MessageBoxWidgetDto,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import {
  isMessageBox,
  isProgressBar,
  isQuantityButtons,
} from "@src/store/slices/widgets/util";

export const getWidgetColorForField = (
  field: string,
  widgetColors?: any
): string | null => {
  if (!widgetColors) return null;

  switch (field) {
    case "backgroundColors.buttonBorderColor":
      return widgetColors.border || null;
    case "backgroundColors.badgeColor":
    case "backgroundColors.freeShippingBackground":
    case "backgroundColors.freeGiftBackground":
    case "textColors.sectionTitle":
    case "textColors.buttonTitle":
    case "textColors.label":
    case "textColors.subtitle":
    case "textColors.price":
    case "textColors.comparePrice":
    case "textColors.badge":
    case "textColors.freeShippingLabel":
    case "textColors.freeGiftLabel":
    case "textColors.freeGiftCompareAtPrice":
    case "textColors.dynamicProgressText":
    case "textColors.breakpointTextInactive":
    case "textColors.breakpointTextActive":
    case "bar.activeColor":
    case "bar.inactiveColor":
    case "breakpoint.activeColor":
    case "breakpoint.inactiveColor":
    case "breakpoint.activeIconColor":
    case "breakpoint.inactiveIconColor":
    case "container.backgroundColor":
    case "container.textColor":
      return widgetColors.primary || null;
    default:
      return null;
  }
};

export const getPrimaryColorFromConfig = (
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  colorConfig: any,
  themeColors?: any,
  defaultColor: string = "#000000"
): string => {
  // For progress bars, use breakpoint active color as primary, then fall back to theme, then black
  if (isProgressBar(widget)) {
    if (colorConfig.breakpoint?.activeColor) {
      return colorConfig.breakpoint.activeColor;
    }
    return themeColors?.primary || defaultColor;
  }

  // For message boxes, use container background color as primary
  if (isMessageBox(widget)) {
    if (colorConfig.container?.backgroundColor) {
      return colorConfig.container.backgroundColor;
    }
    return themeColors?.primary || defaultColor;
  }

  // For quantity buttons, check for user-selected primary color in background colors badge
  if (colorConfig.backgroundColors?.badgeColor) {
    return colorConfig.backgroundColors.badgeColor;
  }

  // Fall back to theme colors, then black
  return themeColors?.primary || defaultColor;
};

export const getColorForDisplay = (
  field: string,
  styleOverrideColor: string | undefined,
  widgetColors: any,
  primaryColor: string,
  defaultColor: string = "#000000"
): string => {
  if (styleOverrideColor) return styleOverrideColor;

  const widgetColor = getWidgetColorForField(field, widgetColors);
  if (widgetColor) return widgetColor;

  if (primaryColor !== defaultColor) {
    return primaryColor;
  }

  return defaultColor;
};

// Simple static arrays based on the actual TypeScript interfaces
export const getAllColorFields = (
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto
) => {
  if (isQuantityButtons(widget)) {
    return {
      backgroundColors: [
        "buttonInactiveBackground",
        "buttonActiveBackground",
        "buttonBorderColor",
        "badgeColor",
        "freeShippingBackground",
        "freeGiftBackground",
      ],
      textColors: [
        "sectionTitle",
        "buttonTitle",
        "label",
        "subtitle",
        "price",
        "comparePrice",
        "badge",
        "freeShippingLabel",
        "freeGiftLabel",
        "freeGiftCompareAtPrice",
      ],
    };
  }

  if (isProgressBar(widget)) {
    return {
      bar: ["activeColor", "inactiveColor"],
      breakpoint: [
        "activeColor",
        "inactiveColor",
        "activeIconColor",
        "inactiveIconColor",
      ],
      textColors: [
        "dynamicProgressText",
        "breakpointTextInactive",
        "breakpointTextActive",
      ],
    };
  }

  if (isMessageBox(widget)) {
    return {
      container: ["backgroundColor", "textColor"],
    };
  }

  // Default fallback
  return {
    backgroundColors: [
      "buttonInactiveBackground",
      "buttonActiveBackground",
      "buttonBorderColor",
      "badgeColor",
      "freeShippingBackground",
      "freeGiftBackground",
    ],
    textColors: [
      "sectionTitle",
      "buttonTitle",
      "label",
      "subtitle",
      "price",
      "comparePrice",
      "badge",
      "freeShippingLabel",
      "freeGiftLabel",
      "freeGiftCompareAtPrice",
      "dynamicProgressText",
      "breakpointTextInactive",
      "breakpointTextActive",
    ],
    bar: ["activeColor", "inactiveColor"],
    breakpoint: [
      "activeColor",
      "inactiveColor",
      "activeIconColor",
      "inactiveIconColor",
    ],
  };
};
